import {CLStore} from 'common/src/redux/store';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {addOrUpdateCardInputSchema} from '../../../../schemas/baseSchemas';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import standaloneAgent from 'common/src/modules/shared/copilot/utils/creators/standaloneAgent';
import {responseSchema} from '../../promptToCardAgentV2.schemas';
import getSetupAgentModel from '../../../../utils/getSetupAgentModel';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {cardTypeToName} from '../../../../campaignSetupAgent.config';
import getPromptToCardAgentInstructions from '../../../promptToCardAgent/utils/getPromptToCardAgentInstructions';
import setCardNameTool from '../general/setCardNameTool';
import setCardDescriptionTool from '../general/setCardDescriptionTool';
import getFilesTool from 'common/src/modules/shared/copilot/tools/getFilesTool';
import analyzeExcelHeadersTool from 'common/src/modules/shared/copilot/tools/analyzeExcelHeadersTool/analyzeExcelHeadersTool';
import readValuesFromExcelTool from '../../../../tools/general/readValuesFromExcelTool/readValuesFromExcelTool';
import getCardDetailsTool from '../general/getCardDetailsTool';
import preProcessStandaloneAgent from '../../utils/general/preProcessStandaloneAgent';
import postProcessStandaloneAgent from '../../utils/general/postProcessStandaloneAgent';
import {WeightedCardScratchpadState} from '../../scratchpad/weightedCardScratchpad.types';
import getOrCreateWeightedCard from '../../utils/weighted/getOrCreateWeightedCard';
import setShouldSplitAudienceTool from './setShouldSplitAudienceTool';
import addWeightedOutputTool from './addWeightedOutputTool';
import editWeightedOutputTool from './editWeightedOutputTool';
import deleteWeightedOutputsTool from './deleteWeightedOutputsTool';

const cardSpecificInstructions = `### What is a Weighted card
A Weighted card distributes traffic across outputs based on relative weights. Higher weight means more traffic to that output.

### Weighted card rules
- Weighted cards do NOT have fallback outputs.
- Must have at least 2 outputs at all times.
- Weights are relative values; they do not need to sum to 100.

### Property Dependencies
- All properties (audience split, outputs, card name, description) are independent — none depend on another being set first.`;

export default function addOrUpdateWeightedCardToolV2(store: CLStore) {
  async function execute(
    params: ToolExecuteParams<typeof addOrUpdateCardInputSchema, WeightedCardScratchpadState>,
  ) {
    const {toolUtils, callChain} = params;

    const {promptToUse, normalizedCardId, card} = await preProcessStandaloneAgent(
      params,
      store,
      getOrCreateWeightedCard,
    );

    const standaloneAgentDef = standaloneAgent({
      name: V2CardAgentToolName.AddOrUpdateWeightedCard,
      role: `Your purpose is to convert a natural language prompt to a ${
        cardTypeToName[NodeType.WEIGHTED]
      } card`,
      instructions: getPromptToCardAgentInstructions({
        cardType: NodeType.WEIGHTED,
        cardId: normalizedCardId,
        cardSpecificInstructions,
      }),
      tools: [
        setCardNameTool(),
        setCardDescriptionTool(),
        getFilesTool(),
        getCardDetailsTool(),
        analyzeExcelHeadersTool(),
        readValuesFromExcelTool(),

        // Weighted tools
        setShouldSplitAudienceTool(),
        addWeightedOutputTool(),
        editWeightedOutputTool(),
        deleteWeightedOutputsTool(),
      ],
      responseSchema,
      model: getSetupAgentModel(),
    });

    const toolScratchpad = {card};

    const result = await toolUtils.invokeStandaloneAgent({
      agent: standaloneAgentDef,
      prompt: promptToUse,
      callChain,
      scratchpad: toolScratchpad,
    });

    return postProcessStandaloneAgent({
      result: {status: result.status, chainOfThoughts: result.chainOfThoughts},
      scratchpad: toolScratchpad,
      store,
      cardId: normalizedCardId,
    });
  }

  return tool({
    name: V2CardAgentToolName.AddOrUpdateWeightedCard,
    description:
      'Add or update a Weighted card. Distributes traffic across outputs based on relative weights. Supports reading values from Excel.',
    displayName: 'Working on weighted card',
    inputSchema: addOrUpdateCardInputSchema,
    execute,
  });
}
